import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { disclaimer } from '../mock/data';

const DisclaimerModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef(null);
  const acceptBtnRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    const hasSeenDisclaimer = localStorage.getItem('hasSeenDisclaimer');
    if (!hasSeenDisclaimer) {
      previousFocusRef.current = document.activeElement;
      setIsOpen(true);
    }
  }, []);

  // Focus the accept button when modal opens
  useEffect(() => {
    if (isOpen && acceptBtnRef.current) {
      acceptBtnRef.current.focus();
    }
  }, [isOpen]);

  // Focus trap and Escape key handling
  const handleKeyDown = useCallback((e) => {
    if (!isOpen || !modalRef.current) return;

    if (e.key === 'Escape') {
      handleDecline();
      return;
    }

    if (e.key === 'Tab') {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleAccept = () => {
    localStorage.setItem('hasSeenDisclaimer', 'true');
    setIsOpen(false);
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  };

  const handleDecline = () => {
    window.location.href = 'https://www.sebi.gov.in';
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-title"
      aria-describedby="disclaimer-content"
      ref={modalRef}
    >
      <div className="relative w-full max-w-2xl mx-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-amber-500/20">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-500/20 bg-gradient-to-r from-amber-900/20 to-transparent">
          <div className="p-2 bg-amber-500/10 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-amber-500" aria-hidden="true" />
          </div>
          <h2 id="disclaimer-title" className="text-2xl font-bold text-amber-500">{disclaimer.title}</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto custom-scrollbar" id="disclaimer-content" tabIndex={0} role="document">
          <div className="space-y-4 text-gray-300 leading-relaxed whitespace-pre-line">
            {disclaimer.content}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-4 px-6 py-4 border-t border-amber-500/20 bg-gradient-to-r from-transparent to-amber-900/10">
          <button
            onClick={handleDecline}
            className="flex-1 px-6 py-3 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-800 transition-all duration-200"
          >
            Decline
          </button>
          <button
            ref={acceptBtnRef}
            onClick={handleAccept}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-emerald-500 transition-all duration-200 shadow-lg shadow-emerald-900/50"
          >
            I Understand &amp; Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
